import {
   Check,
   ChevronsUpDown,
   Loader2,
} from "lucide-react";

import {
   useEffect,
   useRef,
   useState,
} from "react";

import {
   Popover,
   PopoverContent,
   PopoverTrigger,
} from "../shadcn/popover";

import {
   Command,
   CommandEmpty,
   CommandGroup,
   CommandInput,
   CommandItem,
   CommandList,
} from "../shadcn/command";

import { Button } from "../shadcn/button";
import { cn } from "../../lib/utils";
import { LuEraser } from "react-icons/lu";

export function AsyncSelect({
   fetchOptions,
   fetchOption,
   getOptionValue,
   getOptionLabel,
   value,
   onChange,
   label,
   placeholder = "Selecione...",
   searchPlaceholder = "Buscar...",
   emptyMessage = "Nenhum resultado encontrado.",
   clearable = true,
   disabled = false,
   renderOption,
   debounceMs = 400,
   className,
   ...props
}) {
   const [options, setOptions] = useState([]);
   const [selectedOption, setSelectedOption] = useState(null);
   const [loading, setLoading] = useState(false);
   const [open, setOpen] = useState(false);
   const [search, setSearch] = useState("");
   const [error, setError] = useState("");

   useEffect(() => {
      setError(props.error);
   }, [props.error]);

   const abortControllerRef = useRef(null);

   const selectedValue = value ?? "";

   const selectedLabel = value
      ? getOptionLabel(selectedOption)
      : "";

   /**
    * Cancela a requisição atual.
    */
   function abortCurrentRequest() {
      if (abortControllerRef.current) {
         abortControllerRef.current.abort();
         abortControllerRef.current = null;
      }
   }

   /**
    * Executa a busca.
    */
   async function loadOptions(searchValue) {
      abortCurrentRequest();

      const controller = new AbortController();

      abortControllerRef.current = controller;

      try {
         setLoading(true);

         const response = await fetchOptions(
            searchValue,
            controller.signal
         );

         if (controller.signal.aborted) {
            return;
         }

         setOptions(response || []);
      } catch (error) {
         if (controller.signal.aborted) {
            return;
         }

         console.error(
            "Erro ao carregar opções:",
            error
         );
      } finally {
         if (!controller.signal.aborted) {
            setLoading(false);
         }
      }
   }

   /**
    * Busca imediatamente quando o select é aberto.
    */
   useEffect(() => {
      if (!open) {
         return;
      }

      loadOptions("");
   }, [open]);

   /**
    * Debounce SOMENTE quando o search muda.
    */
   useEffect(() => {
      if (!open) {
         return;
      }

      const timeout = setTimeout(() => {
         loadOptions(search);
      }, debounceMs);

      return () => {
         clearTimeout(timeout);
      };
   }, [search]);

   /**
    * Cleanup ao desmontar o componente.
    */
   useEffect(() => {
      return () => {
         abortCurrentRequest();
      };
   }, []);

   useEffect(() => {
      (async () => {
         if (value && !selectedOption) {
            const option = options.find((o) => getOptionValue(o) === value);
            if (option) {
               setSelectedOption(option);
            } else {
               const option = fetchOption ? await fetchOption(value) : null;
               if (option) {
                  setSelectedOption(option);
               }
            }
         }
      })()
   }, [
      selectedOption
   ]);

   function handleSelect(option) {
      setSelectedOption(option);
      onChange(getOptionValue(option) === selectedValue ? null : getOptionValue(option));
      setOpen(false);
      setSearch("");
   }

   function handleClear() {
      onChange(null);
      setOpen(false);
      setSearch("");
      setSelectedOption(null);
   }

   function handleOpenChange(isOpen) {
      setOpen(isOpen);

      if (!isOpen) {
         // setSearch("");
         // setOptions([]);
         abortCurrentRequest();
      }
   }

   return (
      <div
         className={cn(
            "flex flex-col gap-1",
            className
         )}
         {...props}
      >
         {label && (
            <label className="text-left text-[14px]! text-(--textColor)!">
               {label}
            </label>
         )}

         <Popover
            open={open}
            onOpenChange={handleOpenChange}
         >
            <PopoverTrigger asChild>
               <Button
                  type="button"
                  variant="outline"
                  role="combobox"
                  aria-expanded={open}
                  disabled={disabled}
                  className={`
                     justify-between px-3! font-normal border! border-(--borderColor)! rounded-(--borderRadius)! h-[42px]! scale-100! text-(--textColor)!
                     ${error ? 'border-red-500!' : ''}
                  `}
               >
                  <span
                     className={cn(
                        "truncate",
                        !selectedLabel && "opacity-50"
                     )}
                  >
                     {selectedLabel || placeholder}
                  </span>
                  <div className="flex flex-row items-center gap-1">
                     <Loader2 className={`animate-spin opacity-0 duration-300 ${loading ? 'opacity-100' : ''}`} />
                     <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
                  </div>
               </Button>
            </PopoverTrigger>

            <PopoverContent
               className="w-[var(--radix-popover-trigger-width)] p-2.5!"
               align="start"
            >
               <Command shouldFilter={false}>
                  <div className="w-full px-2! box-border!">
                     <CommandInput
                        placeholder={searchPlaceholder}
                        className="h-9"
                        value={search}
                        onValueChange={setSearch}
                     />
                  </div>

                  <CommandList className="mt-2!">
                     {loading ? (
                        <div className="w-full flex flex-col gap-0.5">
                           <div className="w-[80%] h-[32px] rounded-(--borderRadius) shimmer" />
                           <div className="w-[50%] h-[32px] rounded-(--borderRadius) shimmer" />
                           <div className="w-[60%] h-[32px] rounded-(--borderRadius) shimmer" />
                        </div>
                     ) : (
                        <>
                           <CommandEmpty>
                              {emptyMessage}
                           </CommandEmpty>

                           <CommandGroup>
                              {options.map((option) => {
                                 const optionValue =
                                    getOptionValue(option);

                                 const optionLabel =
                                    getOptionLabel(option);

                                 const selected =
                                    selectedValue === optionValue;

                                 return (
                                    <CommandItem
                                       key={optionValue}
                                       value={optionValue}
                                       onSelect={() =>
                                          handleSelect(option)
                                       }
                                    >
                                       {renderOption
                                          ? renderOption(option)
                                          : optionLabel}

                                       <Check
                                          className={cn(
                                             "ml-auto",
                                             selected
                                                ? "opacity-100"
                                                : "opacity-0"
                                          )}
                                       />
                                    </CommandItem>
                                 );
                              })}

                              {clearable && value && (
                                 <CommandItem
                                    value="__clear__"
                                    onSelect={handleClear}
                                    className="*:text-red-500 mt-4! relative"
                                 >
                                    <span className="absolute left-0 -top-2 w-full h-px bg-(--borderColor)!"></span>
                                    <LuEraser size={18} />
                                    Limpar seleção
                                 </CommandItem>
                              )}
                           </CommandGroup>
                        </>
                     )}
                  </CommandList>
               </Command>
            </PopoverContent>
         </Popover>

         {error && (
            <span className="error-message w-full text-left">
               {error}
            </span>
         )}
      </div>
   );
}
